import React, { forwardRef } from 'react';
import { CONFIG } from '../data/config';
import { CheckoutData, CartItemType } from '../types/types';
import { useLanguage } from '../context/LanguageContext';
interface OrderTicketProps {
  orderNumber: string;
  data: CheckoutData;
  items: CartItemType[];
  cartTotal: number;
  totalFinal: number;
}
export const OrderTicket = forwardRef<HTMLDivElement, OrderTicketProps>(({
  orderNumber,
  data,
  items,
  cartTotal,
  totalFinal
}, ref) => {
  const {
    t,
    language,
    formatPrice,
    translateProductName,
    translateIngredient,
    translateSelectionLabel
  } = useLanguage();
  const cartTotalFormatted = formatPrice(cartTotal);
  const expressFeeFormatted = formatPrice(CONFIG.EXPRESS_FEE);
  const totalFinalFormatted = formatPrice(totalFinal);
  // Use language-aware date formatting
  const dateLocale = language === 'es' ? 'es-CR' : language === 'fr' ? 'fr-FR' : language === 'zh' ? 'zh-CN' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : 'en-US';
  return <div ref={ref} className="bg-surface w-[340px] p-6 font-mono text-sm border border-gray-800 shadow-2xl relative overflow-hidden" data-id="element-1054">
        {/* Ticket zig-zag top edge (simulated with CSS) */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_0,transparent_5px,#1A1A1A_6px)]" data-id="element-1055" />

        {/* Header */}
        <div className="text-center mb-6 pt-2" data-id="element-1056">
          <div className="w-16 h-16 mx-auto mb-3 grayscale contrast-200" data-id="element-1057">
            <img src={CONFIG.LOGO_URL} alt={CONFIG.APP_NAME} className="w-full h-full object-contain rounded-full border border-gray-700" data-id="element-1058" />
          </div>
          <h1 className="font-bold text-xl text-white tracking-widest uppercase" data-id="element-1059">
            {CONFIG.APP_NAME}
          </h1>
          <p className="text-xs text-gray-400 mt-1 uppercase tracking-wider" data-id="element-1060">
            {t('tagline')}
          </p>
          <div className="border-b-2 border-dashed border-gray-700 my-4" data-id="element-1061" />
          <p className="font-bold text-lg text-white" data-id="element-1062">
            {t('orderNumber')} #{orderNumber}
          </p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-1063">
            {new Date().toLocaleDateString(dateLocale, {
          weekday: 'short',
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        })}
          </p>
        </div>

        {/* Customer Info */}
        <div className="mb-6 space-y-1 text-gray-300" data-id="element-1064">
          <p data-id="element-1065">
            <span className="font-semibold text-white" data-id="element-1066">{t('client')}:</span>{' '}
            {data.nombre}
          </p>
          <p data-id="element-1067">
            <span className="font-semibold text-white" data-id="element-1068">{t('tel')}:</span>{' '}
            {data.telefono}
          </p>
          <p data-id="element-1069">
            <span className="font-semibold text-white" data-id="element-1070">{t('delivery')}:</span>{' '}
            {data.entrega === 'Retiro en local' ? t('pickupLocal') : t('express')}
          </p>
          <p data-id="element-1071">
            <span className="font-semibold text-white" data-id="element-1072">{t('payment')}:</span>{' '}
            {data.metodoPago === 'Tarjeta' ? t('card') : data.metodoPago === 'Efectivo' ? t('cash') : data.metodoPago}
          </p>
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-1073" />

        {/* Items */}
        <div className="mb-6 space-y-4" data-id="element-1074">
          <div className="flex justify-between font-bold text-white mb-2 pb-2 border-b border-gray-800" data-id="element-1075">
            <span data-id="element-1076">
              {t('qty')} {t('description')}
            </span>
            <span data-id="element-1077">{t('total')}</span>
          </div>

          {items.map((item, idx) => <div key={idx} className="text-gray-300" data-id="element-1078">
              <div className="flex justify-between font-bold text-white" data-id="element-1079">
                <span data-id="element-1080">
                  {item.quantity}x{' '}
                  {translateProductName(item.productId, item.name)}
                </span>
                <span data-id="element-1081">
                  {formatPrice(item.subtotal * item.quantity).primary}
                </span>
              </div>

              <div className="pl-4 text-xs mt-1 space-y-0.5 text-gray-400" data-id="element-1082">
                {item.selections.base && <p data-id="element-1083">
                    {translateSelectionLabel('base')}:{' '}
                    {translateIngredient(item.selections.base.id, item.selections.base.name)}
                  </p>}
                {item.selections.proteina && <p data-id="element-1084">
                    {translateSelectionLabel('proteina')}:{' '}
                    {translateIngredient(item.selections.proteina.id, item.selections.proteina.name)}
                  </p>}
                {item.selections.toppings && item.selections.toppings.length > 0 && <p data-id="element-1085">
                      {t('toppings')}:{' '}
                      {item.selections.toppings.map(tp => translateIngredient(tp.id, tp.name)).join(', ')}
                    </p>}
                {item.selections.toppingsPremium && item.selections.toppingsPremium.length > 0 && <p data-id="element-1086">
                      {translateSelectionLabel('premium')}:{' '}
                      {item.selections.toppingsPremium.map(tp => translateIngredient(tp.id, tp.name)).join(', ')}
                    </p>}
                {item.selections.aderezos && item.selections.aderezos.length > 0 && <p data-id="element-1087">
                      {t('dressing')}:{' '}
                      {item.selections.aderezos.map(ad => translateIngredient(ad.id, ad.name)).join(', ')}
                    </p>}
                {item.selections.semillas && item.selections.semillas.length > 0 && <p data-id="element-1088">
                      {translateSelectionLabel('seeds')}:{' '}
                      {item.selections.semillas.map(sd => translateIngredient(sd.id, sd.name)).join(', ')}
                    </p>}
                {item.selections.comentarios && <p className="italic mt-1" data-id="element-1089">
                    {t('note')}: {item.selections.comentarios}
                  </p>}
              </div>
            </div>)}
        </div>

        <div className="border-b-2 border-dashed border-gray-700 mb-4" data-id="element-1090" />

        {/* Totals */}
        <div className="space-y-1 mb-6 text-gray-300" data-id="element-1091">
          <div className="flex justify-between" data-id="element-1092">
            <span data-id="element-1093">{t('subtotal')}</span>
            <span data-id="element-1094">{cartTotalFormatted.primary}</span>
          </div>
          {data.entrega === 'Express' && <div className="flex justify-between" data-id="element-1095">
              <span data-id="element-1096">{t('express')}</span>
              <span data-id="element-1097">{expressFeeFormatted.primary}</span>
            </div>}
          <div className="flex justify-between font-bold text-lg pt-2 mt-2 border-t border-gray-800 text-white" data-id="element-1098">
            <span data-id="element-1099">{t('total')}</span>
            <span data-id="element-1100">{totalFinalFormatted.primary}</span>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-500 mt-8" data-id="element-1101">
          <p className="font-bold text-gray-400 mb-1" data-id="element-1102">{t('thankYou')}</p>
          <p data-id="element-1103">{t('followUs')}</p>
          <p data-id="element-1104">@{CONFIG.APP_NAME.replace(/\s+/g, '').toLowerCase()}</p>
        </div>

        {/* Ticket zig-zag bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-repeat-x bg-[length:10px_10px] bg-[radial-gradient(circle_at_50%_10px,transparent_5px,#1A1A1A_6px)]" data-id="element-1105" />
      </div>;
});